import styled from 'styled-components'
import Colors from 'Theme/Colors'
import Styles from './ConnectorListItem.module.css'
import {
  IFText,
  ConnectorSelectPicker,
  IFTextInput,
  ConnectorOngoingActions,
  IFsvg,
  IFTooltipIconsLoading,
  IFToggle,
  TariffSelectPicker,
} from 'Components'
import React, {
  useState,
  useImperativeHandle,
  useEffect,
  Fragment,
  useRef,
} from 'react'
import { AccordionDetails, Popover } from '@mui/material'
import PropTypes from 'prop-types'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import { useTranslation } from 'react-i18next'
import Icon from '@mui/material/Icon'
import InfinityEnums from 'Enums/InfinityEnums'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import icons from 'Theme/Icons'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import {
  mapStatus,
  mapTypeReverse,
  getConnectorDisplayStatus,
} from 'Utils/ConnectorFunctions'
import { isPropsMatch } from 'Utils/PropsMatch'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import InfoIcon from '@mui/icons-material/Info'
import { Box, styled as muiStyled } from '@mui/system'
import MuiAccordion from '@mui/material/Accordion'
import { ChargingEventsModal, TariffPopOverContainer } from 'Containers'
import { getEnergyPriceRange } from 'Utils/Calculations'
import { VisibilityOff, VisibilityRounded } from '@mui/icons-material'
import Fade from '@mui/material/Fade'

const { ConnectorPowerLevel } = InfinityEnums
const Accordion = muiStyled(MuiAccordion)({
  boxShadow: 'none',
  borderRadius: '5px',
  '&:before': {
    display: 'none',
  },
})

const AccordionSummary = muiStyled(MuiAccordionSummary)(({ theme }) => ({
  padding: 0,
  minHeight: '2.5rem',
  borderRadius: '5px',
  boxShadow: `0px 0px 2px ${Colors.BoxShadowColor}`,
  '&.Mui-expanded': {
    minHeight: 40,
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    height: '3rem',
    '&.Mui-expanded': {
      margin: 0,
    },
  },
}))

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: Colors.white,
    boxShadow: `0px 0px 4px 0px ${Colors.tooltipBoxShadow}`,
    maxWidth: 'none',
  },
}))

const handleMaxPowerLogo = (maxPower) => {
  if (maxPower <= ConnectorPowerLevel.LEVEL1)
    return <div className={Styles.FlashContainer}>{icons.flash}</div>
  else if (maxPower <= ConnectorPowerLevel.LEVEL2)
    return [...Array(2)].map((e, index) => (
      <div key={index} className={Styles.FlashContainer}>
        {icons.flash}
      </div>
    ))
  else
    return [...Array(3)].map((e, index) => (
      <div key={index} className={Styles.FlashContainer}>
        {icons.flash}
      </div>
    ))
}

const handleIcon = (type) => {
  switch (type) {
    case InfinityEnums.ConnectorType.IEC_62196_T2:
      return icons.type2({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.CHADEMO:
      return icons.chademo({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.IEC_62196_T2_COMBO:
      return icons.comboCCS({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.DOMESTIC_F:
      return icons.schuko({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.GBT_DC:
      return icons.GBT_DC({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.IEC_62196_T1:
      return icons.type1({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.IEC_62196_T1_COMBO:
      return icons.type1CCS({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.GBT_AC:
      return icons.GBT_AC({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    case InfinityEnums.ConnectorType.TESLA_S:
      return icons.NACS({
        width: 24,
        height: 24,
        color: Colors.black,
      })
    default:
      return null
  }
}

const MaxPowerDiv = styled.div`
  background-color: ${Colors.MaxPowerBackground};
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
`
const StatusContainer = styled.div`
  background-color: ${Colors.GreyBackground};
  color: ${(props) => props.status};
`

const LastUpdatedContainer = styled.div`
  border: 2px solid ${Colors.GreyBackground};
  background-color: ${Colors.white};
`

const StyledTooltipToggle = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: `${Colors.primary} !important`,
    color: `${Colors.white} !important`,
    fontSize: '0.75rem !important',
    fontFamily: 'ProximaNova !important',
    padding: '0.3rem !important',
    marginRight: '0.5rem !important',
  },
}))
const ConnectorListItem = React.forwardRef(
  (
    {
      maxPower,
      status,
      type,
      uid,
      lastUpdateTime,
      onUnlockClick,
      onStartClick,
      onStopClick,
      onSyncClick,
      isEdit,
      onMaxPowerChange,
      onTypeChange,
      onTariffChange,
      isStopTxLoading,
      isUnlockLoading,
      isSyncLoading,
      isStartTxDead,
      isStopTxDead,
      isChargePointOffline,
      editPermission,
      session,
      onStatusChange = () => {},
      onReserveClick,
      onCancelReserveClick,
      reservation,
      operatorUser,
      meterValue,
      errorCode,
      vendorErrorCode,
      vendorId,
      fetchConnectorTypesRequestState,
      connectorTypes,
      handleOpenChargingEventsModal,
      connectorTariff,
      tariffsList,
      currency,
      hasOcpiIntegration,
      isLoading,
      isRemoved,
      chargePoint,
      onVisibleChange = () => {},
      socLimit,
      onSocLimitChange = () => {},
      powerType,
    },
    ref,
  ) => {
    const onClickAccordionHandler = () => {
      if (!isEdit)
        setAccordExpanded((expanded) => {
          return !expanded
        })
    }

    const handleStatus = (status) => {
      if (isChargePointOffline) return Colors.OfflineConnector

      switch (status) {
        case InfinityEnums.ConnectorStatus.AVAILABLE:
          return Colors.AvailableConnector
        case InfinityEnums.ConnectorStatus.PREPARING:
          return Colors.PreparingConnector
        case InfinityEnums.ConnectorStatus.CHARGING:
          return Colors.ChargingConnector
        case InfinityEnums.ConnectorStatus.SUSPENDEDEV:
          return Colors.SuspendedConnector
        case InfinityEnums.ConnectorStatus.SUSPENDEDEVSE:
          return Colors.SuspendedConnector
        case InfinityEnums.ConnectorStatus.FINISHING:
          return Colors.FinishingConnector
        // Fine, session-level "plugged in, not drawing" state (folds in the old
        // Finishing / post-charge Preparing); coloured like a suspended state.
        case InfinityEnums.ChargingSessionChargingState.EVCONNECTED:
          return Colors.SuspendedConnector
        // Coarse in-use fallback when no fine session state is available yet.
        case InfinityEnums.ConnectorStatus.OCCUPIED:
          return Colors.OccupiedConnector
        case InfinityEnums.ConnectorStatus.RESERVED:
          return Colors.ReservedConnector
        case InfinityEnums.ConnectorStatus.UNAVAILABLE:
          return Colors.UnAvailableConnector
        case InfinityEnums.ConnectorStatus.OFFLINE:
          return Colors.OfflineConnector
        case InfinityEnums.ConnectorStatus.FAULTED:
          return Colors.FaultedConnector
        default:
          return Colors.black
      }
    }
    // The connector only stores a coarse status now; the fine energy state lives
    // on its active session. Show that where available (falls back to coarse).
    const displayStatus = getConnectorDisplayStatus({ status, session })
    const updateInputValue = (event) => {
      setMaxPowerValue(event.target.value)
      onMaxPowerChange(event.target.value)
    }

    const resetMaxPowerValue = () => {
      setMaxPowerValue(maxPower)
    }

    const resetSocLimitValue = () => {
      setSocLimitValue(socLimit ?? '')
    }

    const { t } = useTranslation()

    const [accordExpanded, setAccordExpanded] = React.useState(false)
    const [uidHover, setUidHover] = useState(false)
    const [isUidCopied, setIsUidCopied] = useState(false)
    const [maxPowerValue, setMaxPowerValue] = useState(maxPower)
    const [socLimitValue, setSocLimitValue] = useState(socLimit ?? '')
    const statusToggleRef = useRef()
    const visibilityToggleRef = useRef()
    const toolTipTitles = [
      {
        title: t('ChargePointDetails.MeterValue'),
        detail: meterValue ? meterValue : '—',
      },
      {
        title: t('ChargePointDetails.ErrorCode'),
        detail: errorCode ? errorCode : '—',
      },
      {
        title: t('ChargePointDetails.VendorErrorCode'),
        detail: vendorErrorCode ? vendorErrorCode : '—',
      },
      {
        title: t('ChargePointDetails.VendorId'),
        detail: vendorId ? vendorId : '—',
      },
    ]
    const [anchorEl, setAnchorEl] = useState(null)
    const [openKey, setOpenKey] = useState(null)
    const open = Boolean(anchorEl) && openKey !== null
    const closeTimeoutRef = useRef(null)

    const handlePopoverOpen = (e, key) => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }
      setAnchorEl(e.currentTarget)
      setOpenKey(key)
    }

    const handlePopoverClose = () => {
      closeTimeoutRef.current = setTimeout(() => {
        setAnchorEl(null)
        setOpenKey(null)
      }, 100)
    }

    const handlePopoverEnter = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }
    }
    useEffect(() => {
      return () => {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current)
        }
      }
    }, [])

    const ChargingEventsModalRef = useRef()
    const toolTipTitle = (title, details, index) => {
      return (
        <div
          key={`toolTipTitle ${index}`}
          className={
            index === 0 ? Styles.ToolTipContainerFirst : Styles.ToolTipContainer
          }
        >
          <div className={Styles.LeftToolTipDetails}>
            <IFText
              style={{ color: Colors.text }}
              className={Styles.ToolTipTitle}
            >
              {title}
            </IFText>
          </div>
          <div className={Styles.RightToolTipDetails}>
            <IFText
              style={{ color: Colors.tooltipText }}
              className={Styles.ToolTipDetail}
            >
              {details}
            </IFText>
          </div>
        </div>
      )
    }
    const { min, max } = getEnergyPriceRange(connectorTariff?.elements || [])
    const resetStatusValue = () => {
      statusToggleRef.current?.setValue(
        mapStatus(status).text === InfinityEnums.ConnectorStatus.AVAILABLE,
      )
    }
    const resetVisibilityValue = () => {
      visibilityToggleRef.current?.setValue(!isRemoved)
    }

    useEffect(() => {
      setToggleValueChanged(!isRemoved)
    }, [isRemoved])

    const closeAccordion = () => {
      setAccordExpanded(false)
    }

    useImperativeHandle(ref, () => ({
      resetMaxPowerValue,
      closeAccordion,
      resetStatusValue,
      resetVisibilityValue,
      resetSocLimitValue,
    }))

    useEffect(() => {
      setMaxPowerValue(maxPower)
    }, [maxPower])

    useEffect(() => {
      setSocLimitValue(socLimit ?? '')
    }, [socLimit])

    useEffect(() => {
      statusToggleRef.current?.setValue(
        mapStatus(status).text === InfinityEnums.ConnectorStatus.AVAILABLE,
      )
    }, [status])

    useEffect(() => {
      visibilityToggleRef.current?.setValue(!isRemoved)
    }, [isRemoved])

    const animateUid = () => {
      setIsUidCopied(!isUidCopied)
      setTimeout(() => setIsUidCopied(false), 2000)
    }

    const permissionArray = [
      InfinityEnums.OperatorUserPermission.CAN_START_TX,
      InfinityEnums.OperatorUserPermission.CAN_STOP_TX,
      InfinityEnums.OperatorUserPermission.CAN_RESERVE_CONNECTOR,
      InfinityEnums.OperatorUserPermission.CAN_CANCEL_RESERVATION,
      InfinityEnums.OperatorUserPermission.CAN_UNLOCK_CONNECTOR,
      InfinityEnums.OperatorUserPermission.CAN_TRIGGER_MESSAGE,
    ]
    const [toggleValueChanged, setToggleValueChanged] = useState(!isRemoved)

    return (
      <div>
        <div>
          <Accordion
            onClick={onClickAccordionHandler}
            expanded={
              isEdit
                ? false
                : accordExpanded &&
                  editPermission &&
                  permissionArray.some((permission) =>
                    operatorUser.permissions.includes(permission),
                  )
            }
          >
            <AccordionSummary>
              <MaxPowerDiv className={Styles.MaxPowerContainer}>
                <div className={Styles.ChargingLogoContainer}>
                  {handleMaxPowerLogo(maxPowerValue)}
                </div>
                <div className={Styles.TextContainer}>
                  {isEdit ? (
                    <IFTextInput
                      className={Styles.TextInputStyle}
                      isFixed={true}
                      fontSize={12}
                      value={maxPowerValue}
                      onChange={updateInputValue}
                    />
                  ) : (
                    <IFText className={Styles.MaxPowerNumber}>
                      {maxPower}
                    </IFText>
                  )}
                  <IFText className={Styles.MaxPowerText}>{`${t(
                    'ConnectorListItem.kW',
                  )}`}</IFText>
                </div>
              </MaxPowerDiv>
              <div className={Styles.ContentContainer}>
                <div className={Styles.ContentDiv}>
                  {isEdit ? (
                    <div className={Styles.SelectPickerContainer}>
                      <ConnectorSelectPicker
                        data={connectorTypes}
                        initialValue={mapTypeReverse(type)}
                        onValueChange={(value) => onTypeChange(value)}
                        isLoading={fetchConnectorTypesRequestState}
                      />
                    </div>
                  ) : (
                    <div className={Styles.LeftDetailsContainer}>
                      <div className={Styles.IconContainer}>
                        {handleIcon(type)}
                      </div>
                      <div
                        className={
                          isEdit ? Styles.UIDContainerEdit : Styles.UIDContainer
                        }
                      >
                        <div>
                          <IFText>{`#${uid}`}</IFText>
                        </div>
                        <div
                          className={Styles.UidCopyContainer}
                          onMouseEnter={() => setUidHover(true)}
                          onMouseLeave={() => setUidHover(false)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {uidHover ? (
                            <div
                              className={Styles.iconContainer}
                              onClick={() => {
                                navigator.clipboard.writeText(uid)
                              }}
                            >
                              <CheckIcon
                                className={
                                  isUidCopied ? Styles.visible : Styles.hide
                                }
                                style={{
                                  color: Colors.primary,
                                }}
                              />
                              <ContentCopyIcon
                                onClick={animateUid}
                                style={{ color: Colors.ClipboardCopy }}
                                className={
                                  !isUidCopied ? Styles.visible : Styles.hide
                                }
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                  {isEdit ? (
                    <div className={Styles.SelectPickerContainer}>
                      <TariffSelectPicker
                        data={tariffsList}
                        initialValue={connectorTariff._id}
                        onValueChange={(value) => onTariffChange(value)}
                        isLoading={fetchConnectorTypesRequestState}
                        currency={currency}
                      />
                    </div>
                  ) : null}

                  {isEdit ? (
                    <div
                      className={Styles.SocLimitEditContainer}
                      style={{
                        visibility:
                          powerType === InfinityEnums.PowerType.DC
                            ? 'visible'
                            : 'hidden',
                      }}
                    >
                      <IFTextInput
                        className={Styles.SocLimitInputStyle}
                        isFixed={true}
                        fontSize={12}
                        value={socLimitValue}
                        placeholder={'SOC'}
                        onChange={(e) => {
                          if (powerType !== InfinityEnums.PowerType.DC) return
                          const val = e.target.value
                          if (val === '' || /^\d+$/.test(val)) {
                            setSocLimitValue(val)
                            onSocLimitChange(val)
                          }
                        }}
                      />
                      <IFText className={Styles.MaxPowerText}>{'%'}</IFText>
                    </div>
                  ) : null}

                  {isEdit ? (
                    <div
                      className={
                        isEdit ? Styles.UIDContainerEdit : Styles.UIDContainer
                      }
                    >
                      <div>
                        <IFText>{`#${uid}`}</IFText>
                      </div>
                      <div
                        className={Styles.UidCopyContainer}
                        onMouseEnter={() => setUidHover(true)}
                        onMouseLeave={() => setUidHover(false)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {uidHover ? (
                          <div
                            className={Styles.iconContainer}
                            onClick={() => {
                              navigator.clipboard.writeText(uid)
                            }}
                          >
                            <CheckIcon
                              className={
                                isUidCopied ? Styles.visible : Styles.hide
                              }
                              style={{
                                color: Colors.primary,
                              }}
                            />
                            <ContentCopyIcon
                              onClick={animateUid}
                              style={{ color: Colors.ClipboardCopy }}
                              className={
                                !isUidCopied ? Styles.visible : Styles.hide
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <div
                    className={
                      isEdit
                        ? Styles.MiddleContentContainerEdit
                        : editPermission
                          ? Styles.MiddleContentContainer
                          : Styles.MiddleContentContainerNoPermissions
                    }
                  >
                    {isEdit ? (
                      isChargePointOffline ? (
                        <StatusContainer
                          status={handleStatus(status)}
                          className={Styles.StatusContainer}
                        >
                          <IFText className={Styles.StatusText}>
                            {InfinityEnums.ConnectorStatus.OFFLINE}
                          </IFText>
                        </StatusContainer>
                      ) : (
                        <div className={Styles.StatusContainerEdit}>
                          <IFToggle
                            ref={statusToggleRef}
                            height={26}
                            fontSize={14}
                            fontWeight={500}
                            marginLeftRight={8}
                            checkedText={
                              InfinityEnums.ConnectorStatus.AVAILABLE
                            }
                            uncheckedText={
                              InfinityEnums.ConnectorStatus.UNAVAILABLE
                            }
                            defaultChecked={
                              mapStatus(status).text ===
                              InfinityEnums.ConnectorStatus.AVAILABLE
                            }
                            onChange={(isChecked) => {
                              onStatusChange(
                                isChecked
                                  ? InfinityEnums.ConnectorStatus.AVAILABLE
                                  : InfinityEnums.ConnectorStatus.UNAVAILABLE,
                              )
                            }}
                            checkedColor={Colors.AvailableConnector}
                            uncheckedColor={Colors.UnAvailableConnector}
                          />
                        </div>
                      )
                    ) : (
                      <div className={Styles.StatusContainerWrapper}>
                        <StatusContainer
                          status={handleStatus(status)}
                          className={Styles.StatusContainer}
                        >
                          <IFText className={Styles.StatusText}>
                            {isChargePointOffline
                              ? InfinityEnums.ConnectorStatus.OFFLINE
                              : status}
                          </IFText>
                        </StatusContainer>
                        <LastUpdatedContainer
                          className={Styles.LastUpdatedContainer}
                        >
                          <IFsvg.LastUpdated
                            height={18}
                            width={18}
                            fill={Colors.LastUpdatedColor}
                            className={Styles.ClockIcon}
                          />
                          <IFText style={{ color: Colors.LastUpdatedColor }}>
                            {lastUpdateTime}
                          </IFText>
                        </LastUpdatedContainer>
                        <div className={Styles.InfoIconConatiner}>
                          <HtmlTooltip
                            title={
                              <div className={Styles.ToolTipWrapper}>
                                {toolTipTitles.map((item, index) =>
                                  toolTipTitle(item.title, item.detail, index),
                                )}
                              </div>
                            }
                            placement="right-start"
                          >
                            <InfoIcon
                              style={{ color: Colors.tooltipGreyBackground }}
                              className={Styles.InfoIcon}
                            />
                          </HtmlTooltip>
                        </div>
                      </div>
                    )}
                  </div>
                  {isEdit ? null : (
                    <div className={Styles.RightDetailsWrapper}>
                      <div
                        className={Styles.StatusContainerWrapper}
                        style={{
                          marginRight: '1.5rem',
                          visibility:
                            powerType === InfinityEnums.PowerType.DC &&
                            socLimit != null
                              ? 'visible'
                              : 'hidden',
                        }}
                      >
                        <StatusContainer className={Styles.SocLimitContainer}>
                          <IFText className={Styles.StatusText}>
                            {t('ConnectorListItem.SocLimit')}
                          </IFText>
                        </StatusContainer>
                        <LastUpdatedContainer
                          className={Styles.LastUpdatedContainer}
                          style={{ justifyContent: 'center' }}
                        >
                          <IFText
                            className={Styles.TariffPrice}
                            style={{ color: Colors.LastUpdatedColor }}
                          >
                            {socLimit != null ? `${socLimit}%` : ''}
                          </IFText>
                        </LastUpdatedContainer>
                      </div>
                      <Fragment>
                        <div className={Styles.StatusContainerWrapper}>
                          <StatusContainer className={Styles.TariffContainer}>
                            <IFText className={Styles.StatusText}>
                              {t('UserTransaction.Tariff')}
                            </IFText>
                          </StatusContainer>
                          <LastUpdatedContainer
                            className={
                              min === max
                                ? `${Styles.TariffPriceContainer} ${Styles.TariffPriceContainerCenter}`
                                : Styles.TariffPriceContainer
                            }
                          >
                            <IFText
                              style={{
                                color: Colors.LastUpdatedColor,
                                width: '100%',
                              }}
                            >
                              {min !== undefined && max !== undefined && (
                                <IFText
                                  className={Styles.TariffPrice}
                                  style={{
                                    textAlign: min === max ? 'center' : 'left',
                                  }}
                                >
                                  {min === max
                                    ? parseFloat(
                                        Number(max).toFixed(
                                          currency.decimalPlaces || 2,
                                        ),
                                      )
                                    : `${parseFloat(
                                        Number(min).toFixed(
                                          currency.decimalPlaces || 2,
                                        ),
                                      )} - ${parseFloat(
                                        Number(max).toFixed(
                                          currency.decimalPlaces || 2,
                                        ),
                                      )}`}
                                  {currency ? ` ${currency.code}` : ''}
                                </IFText>
                              )}
                            </IFText>
                          </LastUpdatedContainer>
                        </div>
                      </Fragment>
                    </div>
                  )}
                </div>
                <div className={Styles.ToggleEditContainer}>
                  <div className="ToggleWrapper">
                    <StyledTooltipToggle
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 60 }}
                      title={
                        toggleValueChanged
                          ? t('ChargePointDetails.VisibleInApp')
                          : t('ChargePointDetails.NotvisibleInApp')
                      }
                      placement="left"
                    >
                      <div className={Styles.ToggleContainer}>
                        {!isLoading && hasOcpiIntegration ? (
                          <IFToggle
                            ref={visibilityToggleRef}
                            CheckedIcon={
                              <VisibilityRounded
                                sx={{
                                  color: Colors.white,
                                  fontSize: '16px !important',
                                }}
                              />
                            }
                            UncheckedIcon={
                              <VisibilityOff
                                sx={{
                                  color: Colors.white,
                                  fontSize: '16px !important',
                                }}
                              />
                            }
                            readOnly={
                              (chargePoint &&
                                chargePoint.isActivated &&
                                !isEdit) ||
                              isLoading
                            }
                            defaultChecked={!isRemoved}
                            onChange={(value) => {
                              onVisibleChange(value)
                              setToggleValueChanged(value)
                            }}
                          />
                        ) : null}
                      </div>
                    </StyledTooltipToggle>
                  </div>
                </div>
                {isEdit ? (
                  <></>
                ) : (
                  <div className={Styles.ArrowContainer}>
                    {accordExpanded ? (
                      <Icon component={KeyboardArrowUpIcon} />
                    ) : (
                      <Icon component={KeyboardArrowDownIcon} />
                    )}
                  </div>
                )}
              </div>
            </AccordionSummary>
            <AccordionDetails className={Styles.ButtonContainer}>
              {(session || reservation) && (
                <div
                  className={Styles.OngoingSessionContainer}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ConnectorOngoingActions
                    chargingTokenUid={
                      session
                        ? session.chargingToken?.huid
                        : reservation?.chargingToken?.huid
                    }
                    chargingTokenId={
                      session
                        ? session.chargingToken?._id
                        : reservation?.chargingToken?._id ??
                          reservation?.chargingToken?.id
                    }
                    status={displayStatus}
                    powerConsumed={
                      session?.transaction?.meterEnd -
                      session?.transaction?.meterStart
                    }
                    tokenType={
                      session
                        ? session?.chargingToken?.type
                        : reservation?.chargingToken?.type
                    }
                    startedAt={session?.startedAt}
                    reservation={reservation}
                    onIconClick={() => {
                      handleOpenChargingEventsModal()
                      ChargingEventsModalRef.current.setLive(true)
                      ChargingEventsModalRef.current.setModalLiveActive(true)
                      ChargingEventsModalRef.current.show()
                    }}
                  />
                </div>
              )}
              {!(isChargePointOffline || isStartTxDead) &&
                operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_START_TX,
                ) && (
                  <IFTooltipIconsLoading
                    onClick={onStartClick}
                    title={t('ConnectorListItem.StartTx')}
                    Icon={IFsvg.Start}
                    FilledIcon={IFsvg.StartFilled}
                    iconClassname={Styles.Icon}
                  />
                )}
              {!(isChargePointOffline || isStopTxDead) &&
                operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_STOP_TX,
                ) && (
                  <IFTooltipIconsLoading
                    onClick={onStopClick}
                    title={t('ConnectorListItem.StopTx')}
                    iconColor={Colors.red}
                    iconBackgroundColor={Colors.red}
                    Icon={IFsvg.Stop}
                    FilledIcon={IFsvg.StopFilled}
                    iconClassname={Styles.Icon}
                    isLoading={isStopTxLoading}
                    animationDisabled={false}
                  />
                )}
              {!isChargePointOffline &&
                status === InfinityEnums.ConnectorStatus.AVAILABLE &&
                !reservation &&
                operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_RESERVE_CONNECTOR,
                ) && (
                  <IFTooltipIconsLoading
                    onClick={onReserveClick}
                    title={t('ConnectorListItem.ReserveConnector')}
                    Icon={IFsvg.ScheduleSend}
                    FilledIcon={IFsvg.ScheduleSendFill}
                    iconClassname={Styles.Icon}
                  />
                )}

              {!isChargePointOffline &&
                reservation &&
                operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_CANCEL_RESERVATION,
                ) && (
                  <IFTooltipIconsLoading
                    onClick={onCancelReserveClick}
                    title={t('ConnectorListItem.CancelConnectorReservation')}
                    Icon={IFsvg.CancelScheduleSend}
                    FilledIcon={IFsvg.CancelScheduleSendFill}
                    iconClassname={Styles.Icon}
                    animationDisabled={true}
                  />
                )}
              {!isChargePointOffline &&
                operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_UNLOCK_CONNECTOR,
                ) && (
                  <IFTooltipIconsLoading
                    onClick={onUnlockClick}
                    title={t('ConnectorListItem.Unlock')}
                    Icon={IFsvg.Unlock}
                    FilledIcon={IFsvg.UnlockFilled}
                    isLoading={isUnlockLoading}
                    iconClassname={Styles.Icon}
                    animationDisabled={false}
                  />
                )}
              {!isChargePointOffline &&
                operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_TRIGGER_MESSAGE,
                ) && (
                  <IFTooltipIconsLoading
                    onClick={onSyncClick}
                    title={t('ConnectorListItem.RefreshStatus')}
                    Icon={IFsvg.Sync}
                    FilledIcon={IFsvg.Sync}
                    isLoading={isSyncLoading}
                    iconClassname={Styles.Icon}
                    animationDisabled={false}
                  />
                )}
            </AccordionDetails>
          </Accordion>
        </div>

        <ChargingEventsModal
          ref={ChargingEventsModalRef}
          chargingSessionId={session?._id}
          session={session}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
          disableRestoreFocus
          slotProps={{
            paper: {
              onMouseEnter: handlePopoverEnter,
              onMouseLeave: handlePopoverClose,
            },
          }}
        >
          <Box
            className={Styles.ParentBox}
            sx={{
              p: 2,
              maxWidth: 400,
            }}
          >
            <div className={Styles.PopoverContent}>
              <TariffPopOverContainer
                elements={connectorTariff.elements}
                tariff={connectorTariff}
                operator={operatorUser.operator}
              />
            </div>
          </Box>
        </Popover>
      </div>
    )
  },
)
ConnectorListItem.propTypes = {
  maxPower: PropTypes.number,
  status: PropTypes.string,
  type: PropTypes.string,
  uid: PropTypes.number,
  lastUpdateTime: PropTypes.object,
  onUnlockClick: PropTypes.func,
  onStartClick: PropTypes.func,
  onStopClick: PropTypes.func,
  isEdit: PropTypes.bool,
  onMaxPowerChange: PropTypes.func,
  onTypeChange: PropTypes.func,
  isStartTxLoading: PropTypes.bool,
  isStopTxLoading: PropTypes.bool,
  isUnlockLoading: PropTypes.bool,
  editPermission: PropTypes.bool,
  session: PropTypes.object,
  onSyncClick: PropTypes.func,
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'maxPower',
    'status',
    'type',
    'uid',
    'lastUpdateTime',
    'isEdit',
    'isStopTxLoading',
    'isUnlockLoading',
    'isSyncLoading',
    'isStartTxDead',
    'isStopTxDead',
    'isChargePointOffline',
    'editPermission',
    'session',
    'fetchConnectorTypesRequestState',
    'connectorTypes',
    'reservation',
    'socLimit',
  ])
}

export default React.memo(ConnectorListItem, shouldSkipRender)
